import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, UserPlus, UserCheck, Users, MoreVertical, UserMinus, ShieldOff, ShieldCheck } from 'lucide-react';

import { communityService, ConnectionStatus } from '../../services/communityService';
import toast from 'react-hot-toast';

interface UserActionButtonsProps {
  userId: string;
  /** Compact mode shows icon-only buttons (good for inline use) */
  compact?: boolean;
  /** Hide follow/friend button (only show message) */
  messageOnly?: boolean;
  /** Called after state changes (useful for parent refresh) */
  onStatusChange?: (status: ConnectionStatus) => void;
}

/**
 * Reusable action buttons for community users:
 *  - "Trimite mesaj" → opens direct message conversation
 *  - "Adaugă prieten" / "Urmărește" / "Prieten ✓" → follow / unfollow
 *  - "..." dropdown → Elimină prieten | Blochează | Deblochează
 *
 * Behavior:
 *  - If no one follows anyone → shows "Adaugă prieten" (follow)
 *  - If I follow but they don't follow me back → shows "Urmărești" (unfollow)
 *  - If they follow me but I don't → shows "Adaugă prieten" (hinted mutual)
 *  - If mutual → shows "Prieten ✓" (still allows unfollow via menu)
 *  - Never shows buttons for self
 *  - If blocked by me → shows "Blocat" + unblock option
 */
export default function UserActionButtons({
  userId,
  compact = false,
  messageOnly = false,
  onStatusChange,
}: UserActionButtonsProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const data = await communityService.getConnectionStatus(userId);
        if (!cancelled) {
          setStatus(data);
          // NOTE: do NOT call onStatusChange here — it's reserved for user-driven
          // follow/unfollow actions. Calling it on mount would trigger parent
          // refresh loops when UserActionButtons is used inside a list that
          // reloads on status change (e.g. FriendsPage).
        }
      } catch {
        // silently ignore - buttons simply won't render fully
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadStatus();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSendMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/community/messages/${userId}`);
  };

  const handleFollowToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!status || submitting) return;

    setSubmitting(true);
    try {
      if (status.iFollow) {
        await communityService.unfollowUser(userId);
        const newStatus = { ...status, iFollow: false, areFriends: false };
        setStatus(newStatus);
        onStatusChange?.(newStatus);
        toast.success('Utilizator eliminat din lista ta');
      } else {
        await communityService.followUser(userId);
        const newStatus = {
          ...status,
          iFollow: true,
          areFriends: status.theyFollowMe,
        };
        setStatus(newStatus);
        onStatusChange?.(newStatus);
        if (newStatus.areFriends) {
          toast.success('🎉 Sunteți prieteni acum!');
        } else {
          toast.success(
            status.theyFollowMe
              ? 'Prieten adăugat!'
              : 'Ai adăugat utilizatorul. Când te va urmări și el, veți fi prieteni.',
            { duration: 3500 }
          );
        }
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Eroare la actualizarea conexiunii');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnfriend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuOpen(false);
    if (!status || submitting) return;
    setSubmitting(true);
    try {
      await communityService.unfriendUser(userId);
      const newStatus = { ...status, iFollow: false, areFriends: false, theyFollowMe: false };
      setStatus(newStatus);
      onStatusChange?.(newStatus);
      toast.success('Prieten eliminat');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Eroare la eliminarea prietenului');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBlock = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuOpen(false);
    if (!status || submitting) return;
    if (!window.confirm('Ești sigur că vrei să blochezi acest utilizator? Conexiunile existente vor fi eliminate.')) return;
    setSubmitting(true);
    try {
      await communityService.blockUser(userId);
      const newStatus = { ...status, iFollow: false, areFriends: false, theyFollowMe: false, blockedByMe: true };
      setStatus(newStatus);
      onStatusChange?.(newStatus);
      toast.success('Utilizator blocat');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Eroare la blocare');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuOpen(false);
    if (!status || submitting) return;
    setSubmitting(true);
    try {
      await communityService.unblockUser(userId);
      const newStatus = { ...status, blockedByMe: false };
      setStatus(newStatus);
      onStatusChange?.(newStatus);
      toast.success('Utilizator deblocat');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Eroare la deblocare');
    } finally {
      setSubmitting(false);
    }
  };

  // Don't render anything for self or while loading
  if (loading || !status || status.isSelf) return null;

  // If blocked by them → show minimal info
  if (status.blockedByThem) return null;

  // If blocked by me → show only "Blocat" + unblock option
  if (status.blockedByMe) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={handleUnblock}
          disabled={submitting}
          className={
            compact
              ? 'inline-flex items-center justify-center p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors'
              : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors text-sm font-medium'
          }
          title="Deblocat utilizatorul"
        >
          <ShieldCheck className="w-4 h-4" />
          {!compact && <span>Deblocat</span>}
        </button>
      </div>
    );
  }

  // Render message button only (when messageOnly prop set or in tiny context)
  if (messageOnly) {
    return (
      <button
        onClick={handleSendMessage}
        className={
          compact
            ? 'p-2 rounded-full hover:bg-blue-50 text-blue-600 transition-colors'
            : 'flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium'
        }
        title="Trimite mesaj privat"
      >
        <MessageCircle className={compact ? 'w-4 h-4' : 'w-4 h-4'} />
        {!compact && <span>Mesaj</span>}
      </button>
    );
  }

  // Determine friend button appearance
  let friendIcon = <UserPlus className="w-4 h-4" />;
  let friendLabel = 'Adaugă prieten';
  let friendClasses = 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600';

  if (status.areFriends) {
    friendIcon = <Users className="w-4 h-4" />;
    friendLabel = 'Prieten';
    friendClasses = 'bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-600 border border-green-300 hover:border-red-300';
  } else if (status.iFollow) {
    friendIcon = <UserCheck className="w-4 h-4" />;
    friendLabel = 'Urmărești';
    friendClasses = 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600 border border-gray-300 hover:border-red-300';
  } else if (status.theyFollowMe) {
    friendIcon = <UserPlus className="w-4 h-4" />;
    friendLabel = 'Acceptă prieten';
    friendClasses = 'bg-purple-600 text-white hover:bg-purple-700 border border-purple-600';
  }

  const base = compact
    ? 'inline-flex items-center justify-center p-2 rounded-full transition-colors text-xs'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium';

  return (

    <div className={compact ? 'flex items-center gap-1' : 'flex items-center gap-2'} onClick={(e) => e.stopPropagation()}>
      {/* Message button */}
      <button
        onClick={handleSendMessage}
        className={`${base} bg-white text-blue-600 border border-blue-200 hover:bg-blue-50`}
        title="Trimite mesaj privat"
        aria-label="Trimite mesaj"
      >
        <MessageCircle className="w-4 h-4" />
        {!compact && <span>Mesaj</span>}
      </button>

      {/* Follow / Friend button */}
      <button
        onClick={handleFollowToggle}
        disabled={submitting}
        className={`${base} ${friendClasses} disabled:opacity-60 disabled:cursor-not-allowed`}
        title={
          status.areFriends
            ? 'Sunteți prieteni.'
            : status.iFollow
            ? 'Îl urmărești. Apasă pentru a renunța.'
            : status.theyFollowMe
            ? 'Te urmărește. Acceptă pentru a deveni prieteni.'
            : 'Adaugă ca prieten (încep să îl urmărești)'
        }
      >
        {submitting ? (
          <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : status.areFriends && !compact ? (
          <>
            <Users className="w-4 h-4" />
            <span>Prieten</span>
          </>
        ) : (
          <>
            {friendIcon}
            {!compact && <span>{friendLabel}</span>}
          </>
        )}
      </button>

      {/* "..." more options dropdown — always visible */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuOpen(v => !v); }}
          className={`${base} bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700`}
          title="Mai multe opțiuni"
          aria-label="Mai multe opțiuni"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        {menuOpen && (
          <div className="fixed z-[9999] w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1"
            style={{
              top: menuRef.current
                ? menuRef.current.getBoundingClientRect().bottom + 4
                : 0,
              right: menuRef.current
                ? window.innerWidth - menuRef.current.getBoundingClientRect().right
                : 0,
            }}
          >
            {/* Unfriend — only when connected */}
            {(status.areFriends || status.iFollow) && (
              <button
                onClick={handleUnfriend}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <UserMinus className="w-4 h-4 text-orange-500" />
                <span>Elimină prieten</span>
              </button>
            )}

            {/* Block */}
            <button
              onClick={handleBlock}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <ShieldOff className="w-4 h-4" />
              <span>Blochează utilizatorul</span>
            </button>
          </div>
        )}
      </div>

    </div>
  );
}

/**
 * Small variant that shows just icons, for use inside posts or comment rows.
 */
export function UserActionIcons(props: Omit<UserActionButtonsProps, 'compact'>) {
  return <UserActionButtons {...props} compact />;
}
